import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircle from '@mui/icons-material/CheckCircle';

import { COLOR_GREY_400, COLOR_GREY_600, COLOR_PRIMARY_1, COLOR_PRIMARY_2 } from 'src/colors';
import {
  CountryCodeWithCurrency,
  CurrencyWithSubscriptionPrice,
  ProductIDOptions,
  exchangeRates,
} from 'src/const';
import { formatCurrencyBasedOnCountryCode } from 'src/utils/functions';
import { callApi } from 'src/core/callApi';

const expertFeatures = [
  'Map Dynamic Data Updating',
  'Amenities and Performance Filters',
  'Custom Market',
  '3 years Historical Data',
];

const FeatureList = () => {
  return (
    <List disablePadding={true} dense={true}>
      {expertFeatures.map((feature, index) => (
        <ListItem key={index}>
          <ListItemIcon>
            <CheckCircle style={{ color: 'green' }} />
          </ListItemIcon>
          <ListItemText primary={feature} />
        </ListItem>
      ))}
    </List>
  );
};

const successAlert = (text: string) =>
  toast.success(text, {
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'colored',
    draggablePercent: 60,
  });

const errorAlert = (text: string) =>
  toast.error(text, {
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'colored',
    draggablePercent: 60,
  });

const UpgradeSubscriptionModal = ({ open, setOpen }) => {
  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userLocalCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const subscriptionPriceAccordingToUserLocalCurrency =
    CurrencyWithSubscriptionPrice[userLocalCurrency];
  const [duration, setDuration] = useState('yearly');
  const [proratedPrice, setProratedPrice] = useState<null | number>(null);
  const [subscriptionUpgradeLoading, setSubscriptionUpgradeLoading] = useState(false);

  const price =
    duration == 'monthly'
      ? formatCurrencyBasedOnCountryCode(
          parseFloat(subscriptionPriceAccordingToUserLocalCurrency['expert']['monthly'].toFixed(2)),
          userLocalCurrency
        )
      : formatCurrencyBasedOnCountryCode(
          parseFloat(
            (subscriptionPriceAccordingToUserLocalCurrency['expert'][duration] / 12).toFixed()
          ),
          userLocalCurrency
        );

  const handleClose = () => {
    setOpen(false);
  };

  const getProratedUpgradePrice = async () => {
    setProratedPrice(null);

    try {
      const url =
        'https://9s2r54r64l.execute-api.us-east-2.amazonaws.com/default/upgradeStripeSubscription';
      const testProductID = 'price_1PQoO6BVoeq938b5NtkhWK5L';
      const config = {
        queryParams: {
          newProductID: ProductIDOptions['expert'].productID[duration],
          newProductName: 'expert',
          newDuration: duration,
          operation: 'fetch_proration_price',
          //   isTest: true,
        },
      };
      const resp = await callApi(url, config, true);
      const prorationAmt = resp.proration_amount;
      if (prorationAmt && !isNaN(Number(prorationAmt))) {
        setProratedPrice(prorationAmt);
      } else {
        setProratedPrice(subscriptionPriceAccordingToUserLocalCurrency['expert'][duration]);
      }
    } catch (err) {
      setProratedPrice(subscriptionPriceAccordingToUserLocalCurrency['expert'][duration]);
    }
  };

  useEffect(() => {
    getProratedUpgradePrice();
  }, [duration]);

  const upgradeSubscription = async () => {
    try {
      setSubscriptionUpgradeLoading(true);
      const url =
        'https://9s2r54r64l.execute-api.us-east-2.amazonaws.com/default/upgradeStripeSubscription';
      const testProductID = 'price_1PQoO6BVoeq938b5NtkhWK5L';
      const config = {
        queryParams: {
          newProductID: ProductIDOptions['expert'].productID[duration],
          newProductName: 'expert',
          newDuration: duration,
          operation: 'modify_subscription',
          //   isTest: true,
        },
      };
      const resp = await callApi(url, config, true);
      setSubscriptionUpgradeLoading(false);
      if (resp.subscription_modification_status == 'success') {
        successAlert('Subscription plan upgraded successfully!');
      } else {
        errorAlert(
          'Failed to upgrade subscription. Please try again or contact support@airbtics.com.'
        );
      }
    } catch (err) {
      setSubscriptionUpgradeLoading(false);
      errorAlert(
        'Failed to upgrade subscription. Please try again or contact support@airbtics.com.'
      );
    }
    handleClose();
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} sx={{ zIndex: 2000 }}>
        <Grid
          container
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{
            background: 'white',
            width: '500px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: 4,
            outline: 'none !important',
            padding: 3,
          }}
        >
          <Grid
            item
            xs={12}
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5" sx={{ color: COLOR_PRIMARY_2, fontWeight: 500 }}>
              Upgrade to Expert Plan
            </Typography>
            <CloseIcon
              sx={{ color: COLOR_GREY_400, cursor: 'pointer', fontWeight: 'bold' }}
              onClick={handleClose}
            />
          </Grid>
          <Grid item xs={12} sx={{ margin: '10px 0', width: '100%' }}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <FeatureList />
          </Grid>
          <Grid container item direction="row" justifyContent="flex-start" xs={12}>
            <Typography style={{ fontWeight: 'bold', marginTop: '20px' }}>
              Upgrade Summary
            </Typography>
          </Grid>
          <Grid
            item
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            xs={12}
          >
            <Typography variant="body2">Plan</Typography>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={duration}
                onChange={(event, value) => setDuration(value)}
              >
                <FormControlLabel value="yearly" control={<Radio />} label="Yearly" />
                <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid
            item
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            xs={12}
          >
            <Typography variant="body2">New Price</Typography>
            <Typography variant="body2">
              {exchangeRates[userLocalCurrency].symbol}
              {price}{' '}
              <span
                style={{
                  fontSize: '14px',
                  color: COLOR_GREY_600,
                  textDecoration: 'none !important',
                }}
              >
                /month
              </span>
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ margin: '10px 0', width: '100%' }}>
            <Divider />
          </Grid>
          <Grid
            item
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            xs={12}
            sx={{ marginBottom: '10px' }}
          >
            <Typography variant="body2">
              Subtotal (per {duration == 'monthly' ? 'month' : 'year'})
            </Typography>
            <Typography variant="body2">
              {exchangeRates[userLocalCurrency].symbol}
              {formatCurrencyBasedOnCountryCode(
                parseFloat(
                  subscriptionPriceAccordingToUserLocalCurrency['expert'][duration].toFixed(2)
                ),
                userLocalCurrency
              )}
            </Typography>
          </Grid>
          <Grid
            item
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            xs={12}
          >
            <Typography variant="body2">Total Amount Due Today</Typography>
            {proratedPrice ? (
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: COLOR_PRIMARY_2 }}>
                {exchangeRates[userLocalCurrency].symbol}
                {formatCurrencyBasedOnCountryCode(proratedPrice, userLocalCurrency)}
              </Typography>
            ) : (
              <CircularProgress />
            )}
          </Grid>
          <Grid
            item
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            xs={12}
            sx={{ marginTop: '25px' }}
          >
            <Button
              sx={{
                background: COLOR_PRIMARY_2,
                color: 'white',
                width: '100%',
                ':hover': {
                  background: COLOR_PRIMARY_1,
                  color: 'white',
                },
              }}
              onClick={upgradeSubscription}
            >
              Confirm Changes {subscriptionUpgradeLoading && <CircularProgress />}
            </Button>
          </Grid>
        </Grid>
      </Modal>
      <ToastContainer position="bottom-left" newestOnTop />
    </>
  );
};

export default UpgradeSubscriptionModal;
